import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { SponsoredOnboardingModal } from "@/components/SponsoredOnboardingModal";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired") { setState("error"); return; }
      } catch (e) { logger.debug("payment status poll retry", e?.message); /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-md mx-auto py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (<><Loader2 className="h-10 w-10 mx-auto animate-spin text-accent" /><p className="mt-4 text-muted-foreground">Confirming your payment…</p></>)}
      {state === "success" && (
        <>
          <CheckCircle2 className="h-14 w-14 mx-auto text-green-600" />
          <h1 className="font-heading text-2xl font-bold mt-4">You're all set! 🎉</h1>
          <p className="text-muted-foreground mt-2">Your subscription is active. Enjoy your higher daily limits and job-matching features.</p>
          <Button className="mt-6" onClick={() => navigate("/tailor?first=1")} data-testid="success-continue">Tailor a CV now</Button>
          <SponsoredOnboardingModal />
        </>
      )}
      {state === "timeout" && (<><Loader2 className="h-10 w-10 mx-auto text-amber-500" /><h1 className="font-heading text-xl font-bold mt-4">Still processing…</h1><p className="text-muted-foreground mt-2">Your payment is taking a moment. Check the Billing page shortly.</p><Link to="/billing"><Button variant="outline" className="mt-6">Go to Billing</Button></Link></>)}
      {state === "error" && (<><XCircle className="h-12 w-12 mx-auto text-red-500" /><h1 className="font-heading text-xl font-bold mt-4">Something went wrong</h1><p className="text-muted-foreground mt-2">We couldn't confirm this payment.</p><Link to="/billing"><Button variant="outline" className="mt-6">Back to Billing</Button></Link></>)}
    </div>
  );
}
